import { SITE } from "@/lib/site";
import { HEADER_PROBE_PX, heroHeader } from "@/components/hero/heroHeader";
import { Logo } from "@/components/Logo";
import { ButtonLink } from "@/components/ui/Button";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/cn";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
} from "motion/react";
import {
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

const NAV = [
  { label: "Home", href: `${SITE}#top` },
  { label: "Meet Somo", href: `${SITE}/meet-somo` },
  { label: "About", href: `${SITE}/about` },
  { label: "Get a quote", href: `${SITE}/get-a-quote` },
];

const MENU = [
  ...NAV.filter((item) => item.label !== "Get a quote"),
  { label: "Contact", href: `${SITE}/contact` },
];
const QUOTE_HREF = `${SITE}/get-a-quote`;

const SCROLLED_AFTER_PX = 40;

type Tone = "transparent" | "light" | "dark";

export function Header() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const overHeroPhoto = useOverHeroPhoto();
  const scrolledTone = useScrolledTone();
  // Once scrolling starts, the hero photo reads as dark content behind the bar.
  const tone: Tone =
    scrolledTone !== "transparent" && overHeroPhoto ? "dark" : scrolledTone;

  const logo = (logoTone: Tone) => (
    <a href={`${SITE}#top`} aria-label="Somo AI" className="flex items-center">
      <Logo
        className={cn(
          "size-10 transition-colors duration-500",
          logoTone === "light" ? "text-brand" : "text-inverse",
          logoTone !== "light" && "drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]",
        )}
      />
    </a>
  );
  const navLinks = (items: typeof NAV, className: string) => (
    <div
      className={cn(
        "flex items-center gap-4 text-sm uppercase leading-relaxed tracking-nav",
        className,
      )}
    >
      {items.map((item) => (
        <a
          key={item.label}
          href={item.href}
          className={cn(
            "rounded-lg px-3 py-1.5 transition-[opacity,background-color]",
            tone === "light" ? "hover:bg-black/[0.06]" : "hover:bg-white/15",
            isCurrent(item.href, path)
              ? "font-bold"
              : "font-medium opacity-50 hover:opacity-100",
          )}
        >
          {item.label}
        </a>
      ))}
    </div>
  );

  return (
    // A sticky band whose negative margin cancels its height, so the header
    // takes no space; the bar hangs below it. The band pins partly above the
    // viewport, so the bar scrolls up with the page and then stays: 40px to
    // 16px on phones, 56px to 24px from tablets up.
    <header className="pointer-events-none sticky -top-6 z-50 -mb-10 h-10 md:-top-8 md:-mb-14 md:h-14">
      <div className="absolute inset-x-0 top-full px-4 lg:px-10">
        <nav
          aria-label="Main"
          className={cn(
            "pointer-events-auto mx-auto hidden w-fit grid-cols-[1fr_auto_1fr] items-center gap-8 rounded-2xl px-6 py-3 md:grid",
            toneClass(tone),
          )}
        >
          {navLinks(NAV.slice(0, 2), "justify-end")}
          {logo(tone)}
          {navLinks(NAV.slice(2), "justify-start")}
        </nav>
        <MobileNav tone={tone} path={path} logo={logo} />
      </div>
    </header>
  );
}

function MobileNav({
  tone,
  path,
  logo,
}: {
  tone: Tone;
  path: string;
  logo: (tone: Tone) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const reduceMotion = useReducedMotion();
  const menuOpen = open && !isDesktop;
  const barTone = menuOpen ? "transparent" : tone;
  const close = () => setOpen(false);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="pointer-events-auto fixed inset-0 flex flex-col bg-inverse px-8 pb-10 pt-36 text-inverse"
          >
            <ul className="flex flex-col gap-2">
              {MENU.map((item, index) => (
                <motion.li
                  key={item.label}
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.05 + index * 0.04,
                    duration: 0.25,
                    ease: "easeOut",
                  }}
                >
                  <a
                    href={item.href}
                    onClick={close}
                    aria-current={
                      isCurrent(item.href, path) ? "page" : undefined
                    }
                    className={cn(
                      "block py-2 text-3xl leading-tight",
                      !isCurrent(item.href, path) && "text-inverse/60",
                    )}
                  >
                    {item.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            <ButtonLink
              href={QUOTE_HREF}
              onClick={close}
              size="lg"
              className="mt-auto w-full"
            >
              Get a quote
            </ButtonLink>
          </motion.div>
        )}
      </AnimatePresence>
      <nav
        aria-label="Main"
        className={cn(
          "pointer-events-auto relative flex items-center justify-between rounded-2xl p-4 md:hidden",
          toneClass(barTone),
        )}
      >
        {logo(barTone)}
        <button
          type="button"
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setOpen((value) => !value)}
          className="flex size-10 items-center justify-center"
        >
          <svg
            viewBox="0 0 40 40"
            fill="none"
            stroke="currentColor"
            strokeWidth={1}
            aria-hidden
            className="size-10"
          >
            {[-1, 0, 1].map((offset) => (
              <motion.path
                key={offset}
                d={`M4 ${19.5 + offset * 10}h32`}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                initial={false}
                animate={
                  menuOpen
                    ? {
                        y: -offset * 10,
                        rotate: offset * -45,
                        opacity: offset ? 1 : 0,
                      }
                    : { y: 0, rotate: 0, opacity: 1 }
                }
                transition={{
                  duration: reduceMotion ? 0 : 0.25,
                  ease: "easeOut",
                }}
              />
            ))}
          </svg>
        </button>
      </nav>
    </>
  );
}

function toneClass(tone: Tone) {
  return cn(
    "border transition-[background-color,border-color,color,backdrop-filter] duration-500",
    tone === "transparent" && "border-transparent text-inverse",
    tone === "light" &&
      "border-black/5 bg-primary/60 text-primary backdrop-blur-xl",
    tone === "dark" &&
      "border-white/15 bg-inverse/40 text-inverse backdrop-blur-xl",
    tone !== "light" && "text-shadow-soft",
  );
}

// The only anchor link is Home (#top), which is current only on the home page.
function isCurrent(href: string, path: string) {
  return href.includes("#")
    ? path === SITE && href === `${SITE}#top`
    : href === path;
}

function useOverHeroPhoto() {
  const [over, setOver] = useState(heroHeader.photoOpacity.get() > 0.5);
  useMotionValueEvent(heroHeader.photoOpacity, "change", (opacity) =>
    setOver(opacity > 0.5),
  );
  return over;
}

// Whatever sits under the header declares how the header should read against it
// via data-tone; the nearest one wins, so a dark section overrides its page.
function useScrolledTone(): Tone {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener("scroll", onChange, { passive: true });
      window.addEventListener("resize", onChange);
      return () => {
        window.removeEventListener("scroll", onChange);
        window.removeEventListener("resize", onChange);
      };
    },
    () => {
      if (window.scrollY <= SCROLLED_AFTER_PX) {
        return "transparent";
      }
      const behind = document
        .elementsFromPoint(window.innerWidth / 2, HEADER_PROBE_PX)
        .find((el) => !el.closest("header") && el.closest("[data-tone]"));
      const tone = behind?.closest<HTMLElement>("[data-tone]")?.dataset.tone;
      return tone === "dark" ? "dark" : "light";
    },
  );
}
